/**
 * Quick Reaction Form Component
 * Simple form for quick feedback reactions
 */

import React, { useEffect } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, QuickReactionFeedback } from '../../../types/feedback';

interface QuickReactionFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<QuickReactionFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
}

const QuickReactionForm: React.FC<QuickReactionFormProps> = ({
  data,
  onChange,
  validation
}) => {
  const reactionData = data as Partial<QuickReactionFeedback>;

  const reactions = [
    {
      key: 'helpful' as const,
      label: 'Helpful',
      description: 'This page helped me accomplish my task',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
      ),
      color: 'bg-sage-50 border-sage-300 text-sage-700 hover:bg-sage-100',
      selectedColor: 'bg-sage-100 border-sage-400 text-sage-800'
    },
    {
      key: 'confusing' as const,
      label: 'Confusing',
      description: 'Something on this page was unclear or hard to understand',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
      ),
      color: 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100',
      selectedColor: 'bg-amber-100 border-amber-400 text-amber-800'
    },
    {
      key: 'error' as const,
      label: 'Error Found',
      description: 'I encountered an error or something broken',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M15 9l-6 6"/>
          <path d="M9 9l6 6"/>
        </svg>
      ),
      color: 'bg-coral-50 border-coral-300 text-coral-700 hover:bg-coral-100',
      selectedColor: 'bg-coral-100 border-coral-400 text-coral-800'
    },
    {
      key: 'improvement' as const,
      label: 'Could Improve',
      description: 'This page could be better in some way',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ),
      color: 'bg-steel-50 border-steel-300 text-steel-700 hover:bg-steel-100',
      selectedColor: 'bg-steel-100 border-steel-400 text-steel-800'
    }
  ];

  // Auto-validate when reaction is selected
  useEffect(() => {
    if (reactionData.reaction) {
      // Quick reactions are always valid once a reaction is selected
      onChange({
        ...reactionData,
        id: reactionData.id || `reaction-${Date.now()}`,
        timestamp: reactionData.timestamp || new Date(),
        pageUrl: reactionData.pageUrl || window.location.href,
        userAgent: reactionData.userAgent || navigator.userAgent,
        viewport: reactionData.viewport || {
          width: window.innerWidth,
          height: window.innerHeight
        }
      });
    }
  }, [reactionData.reaction]);

  const handleReactionSelect = (reaction: QuickReactionFeedback['reaction']) => {
    onChange({
      ...reactionData,
      reaction,
      type: 'quick-reaction'
    });
  };

  const handleCommentChange = (comment: string) => {
    onChange({
      ...reactionData,
      comment: comment.trim() || undefined
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          How was your experience with this page?
        </h3>
        <p className="text-sm text-slate-600">
          Select the option that best describes your experience
        </p>
      </div>

      {/* Reaction Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {reactions.map((reaction) => (
          <button
            key={reaction.key}
            onClick={() => handleReactionSelect(reaction.key)}
            className={cn(
              'p-6 border-2 rounded-lg text-center transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
              reactionData.reaction === reaction.key
                ? reaction.selectedColor
                : reaction.color
            )}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="flex-shrink-0">
                {reaction.icon}
              </div>
              <div>
                <h4 className="font-medium text-base mb-1">
                  {reaction.label}
                </h4>
                <p className="text-sm opacity-80 leading-snug">
                  {reaction.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Optional Comment */}
      {reactionData.reaction && (
        <div className="space-y-3">
          <label htmlFor="quick-comment" className="block text-sm font-medium text-slate-700">
            Additional comments (optional)
          </label>
          <textarea
            id="quick-comment"
            rows={3}
            value={reactionData.comment || ''}
            onChange={(e) => handleCommentChange(e.target.value)}
            placeholder={`Tell us more about why you found this page ${reactionData.reaction}...`}
            className="input-minimal resize-none"
          />
          <p className="text-xs text-slate-500">
            Help us understand your experience better with a brief comment
          </p>
        </div>
      )}

      {/* Validation Errors */}
      {validation.errors.reaction && (
        <div className="text-sm text-coral-600 bg-coral-50 p-3 rounded-lg">
          {validation.errors.reaction}
        </div>
      )}
    </div>
  );
};

export default QuickReactionForm;